/**
 * Deferred-payment UI helpers (Story 11.6-1).
 *
 * Mirrors the backend rules in order_service.set_order_payment and the design display rule.
 * Shared by OrderDetail.tsx and Orders.tsx so the detail page and the list page can't drift
 * (same lesson as orderCancellation.ts in Story 11.5).
 *
 * Display rule (design): show a payment badge iff `payment_status === 'unpaid' OR paid_at != null`.
 * So a non-deferred order (paid, paid_at null) renders NOTHING; only deferred orders (unpaid, or
 * deferred-then-paid) show payment state.
 *
 * These are UI affordance gates only. The backend guard in set_order_payment is authoritative and
 * returns 403/400 on any mismatch; call sites surface that as a toast.
 */

export type PaymentBadgeKind = "none" | "unpaid" | "overdue" | "paid";

export interface PaymentBadgeState {
  kind: PaymentBadgeKind;
  /** Days past the due date (overdue only, >= 1). */
  daysOverdue?: number;
  /** ISO timestamp the order was marked paid (paid only). */
  paidAt?: string | null;
}

/** Minimal shape needed by the payment helpers (works for Order, OrderDetail, OrderListItem). */
export interface PaymentOrderLike {
  status: string;
  payment_status?: string | null;
  paid_at?: string | null;
  payment_due_date?: string | null;
  source?: string | null;
  created_by?: string | null;
}

/**
 * Whole-day count the due date is in the past (>= 1 means overdue). No grace period
 * (Open Question 4: overdue = payment_due_date < today). Parses the date parts locally to
 * avoid a UTC-vs-local off-by-one on the day boundary.
 */
export function paymentDaysOverdue(dueDate: string, now: Date = new Date()): number {
  const [y, m, d] = dueDate.split("-").map(Number);
  if (!y || !m || !d) return 0;
  const due = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = today.getTime() - due.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/** Compute the payment badge to render for an order (or `none` to render nothing). */
export function paymentBadgeState(
  order: PaymentOrderLike,
  now: Date = new Date()
): PaymentBadgeState {
  const ps = order.payment_status ?? "paid";
  if (ps === "unpaid") {
    // A cancelled order carries no receivable (AC6/AC8): no "Impayé"/"En retard" badge.
    // "Payé le X" below is kept even when cancelled — the cash did come in.
    if (order.status === "cancelled") {
      return { kind: "none" };
    }
    if (order.payment_due_date) {
      const daysOverdue = paymentDaysOverdue(order.payment_due_date, now);
      if (daysOverdue >= 1) {
        return { kind: "overdue", daysOverdue };
      }
    }
    return { kind: "unpaid" };
  }
  // paid: only show a badge for a tracked (deferred-then-paid) order, never a plain order.
  if (order.paid_at) {
    return { kind: "paid", paidAt: order.paid_at };
  }
  return { kind: "none" };
}

/**
 * Can the current user mark this order as paid? (AC7 + AC9, staff scope loosened by Story 11.6-2)
 * unpaid && status != cancelled && (manager/admin any; staff any delivered order, or their own
 * quick sale as a superset — mirrors the backend `set_order_payment` rule exactly).
 */
export function canMarkOrderPaid(
  order: PaymentOrderLike,
  role: string | undefined,
  userId: string | undefined | null
): boolean {
  if ((order.payment_status ?? "paid") !== "unpaid") return false;
  if (order.status === "cancelled") return false;
  if (role === "admin" || role === "manager") return true;
  if (role === "staff") {
    const isOwnQuickSale = order.source === "staff" && !!userId && order.created_by === userId;
    return order.status === "delivered" || isOwnQuickSale;
  }
  return false;
}

/**
 * Can the current user revert this order to unpaid? (AC7 / design Permissions table: manager/admin
 * only — used both to correct a mistaken settlement and to flag an existing order as credit.)
 */
export function canRevertOrderPayment(
  order: PaymentOrderLike,
  role: string | undefined
): boolean {
  if ((order.payment_status ?? "paid") !== "paid") return false;
  if (order.status === "cancelled") return false;
  return role === "admin" || role === "manager";
}
